import {
  gql,
  type ApolloClient,
  type NormalizedCacheObject,
} from '@apollo/client'

export interface BatchedFieldsOptions {
  operationName: string
  fieldName: string
  argumentName: string
  argumentType: 'ID!' | 'String!'
  rootField?: string
  aliasPrefix?: string
  fetchPolicy?: 'cache-first' | 'network-only' | 'no-cache'
}

export const buildBatchedFieldsQuery = (
  values: string[],
  selection: string,
  options: BatchedFieldsOptions
) => {
  if (!values.length) {
    throw new Error('buildBatchedFieldsQuery: values array cannot be empty')
  }

  const {
    operationName,
    fieldName,
    argumentName,
    argumentType,
    rootField,
    aliasPrefix = 'item',
  } = options

  const indexes = values.map((_, index) => index)

  const variables = indexes
    .map(index => `$value${index}: ${argumentType}`)
    .join(', ')

  const fields = indexes
    .map(
      index => `
        ${aliasPrefix}${index}: ${fieldName}(
          ${argumentName}: $value${index}
        ) {
          ${selection}
        }
      `
    )
    .join('\n')

  return gql(`
    query ${operationName}(${variables}) {
      ${rootField ? `${rootField} { ${fields} }` : fields}
    }
  `)
}

export async function fetchBatchedFields<T>(
  client: ApolloClient<Object>,
  values: string[],
  selection: string,
  options: BatchedFieldsOptions
): Promise<(T | null)[]> {
  if (!values.length) return []

  const aliasPrefix = options.aliasPrefix ?? 'item'

  const { data } = await client.query<
    Record<string, unknown>,
    Record<string, string>
  >({
    query: buildBatchedFieldsQuery(values, selection, options),
    variables: Object.fromEntries(
      values.map((value, index) => [`value${index}`, value])
    ),
    fetchPolicy: options.fetchPolicy,
  })

  const result = options.rootField ? data[options.rootField] : data

  if (!result || typeof result !== 'object') {
    return values.map(() => null)
  }

  const fields = result as Record<string, T | null>

  return values.map((_, index) => fields[`${aliasPrefix}${index}`] ?? null)
}

export const buildNodesQuery = (ids: string[], inlineFragment: string) =>
  buildBatchedFieldsQuery(ids, inlineFragment, {
    operationName: 'BatchedNodes',
    fieldName: 'node',
    argumentName: 'id',
    argumentType: 'ID!',
    aliasPrefix: 'node',
  })

export const fetchNodes = <T>(
  client: ApolloClient<NormalizedCacheObject>,
  ids: string[],
  inlineFragment: string
): Promise<(T | null)[]> =>
  fetchBatchedFields<T>(client, ids, inlineFragment, {
    operationName: 'BatchedNodes',
    fieldName: 'node',
    argumentName: 'id',
    argumentType: 'ID!',
    aliasPrefix: 'node',
  })

export interface BatchedRootNodeOptions {
  placeholder?: string
  rootVariableName?: string
}

export const buildBatchedRootNodeQuery = (
  childIds: string[],
  fragmentTemplate: string,
  options: BatchedRootNodeOptions = {}
) => {
  if (!childIds.length) {
    throw new Error('buildBatchedRootNodeQuery: childIds array cannot be empty')
  }

  const placeholder = options.placeholder ?? '%ID%'
  const rootVar = options.rootVariableName ?? 'id'

  const escapedPlaceholder = placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  const fields = childIds
    .map((childId, index) => {
      const fragment = fragmentTemplate.replace(
        new RegExp(escapedPlaceholder, 'g'),
        JSON.stringify(childId)
      )

      return `
        node${index}: node(id: $${rootVar}) {
          ${fragment.trim()}
        }
      `
    })
    .join('\n')

  return gql`
    query BatchedRootNode($${rootVar}: ID!) {
      ${fields}
    }
  `
}

export async function fetchBatchedRootNode<T>(
  client: ApolloClient<NormalizedCacheObject>,
  rootId: string,
  childIds: string[],
  fragmentTemplate: string,
  options: BatchedRootNodeOptions = {}
): Promise<T[]> {
  if (!childIds.length) return []

  const rootVar = options.rootVariableName ?? 'id'

  const { data } = await client.query<Record<string, T | null>>({
    query: buildBatchedRootNodeQuery(childIds, fragmentTemplate, options),
    variables: {
      [rootVar]: rootId,
    },
  })

  return childIds.map(
    (_, index) => data[`node${index}`] ?? (null as unknown as T)
  )
}
